/// <amd-module name="Search/WorkItem/Components/LeftPane/LeftPane" />
import * as React from "react";
import * as Resources from "Search/WorkItem/Resources";
import * as Container from "Search/Shared/Base/Container";
import * as Constants from "Search/WorkItem/Constants";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Intersection } from "VSSUI/Intersection";
import { format } from "VSS/Core/Util/String";
import { SearchStatus } from "Search/Shared/Base/Stores/SearchStore";
import { ResultsInfo } from "Search/Shared/Components/ResultsInfo";
import { ShowMoreRow } from "Search/Shared/Components/ShowMoreRow";
import { ItemsList, IItemsListProps } from "Search/WorkItem/Components/LeftPane/ItemsList";
import { getFieldValue } from "Search/WorkItem/Utils";
import { ActionCreator } from "Search/WorkItem/Flux/ActionCreator";
import { StoresHub, IAggregatedState, StoreName } from "Search/WorkItem/Flux/StoresHub"
import { WorkItemResult, WorkItemSearchRequest } from "Search/LegacyWebApi/Contracts/WorkItem";
import { LoadMore } from "Search/Shared/Components/LoadMore";

interface IScrollableItemsListProps extends IItemsListProps {
    infiniteScroll: boolean;

    moreResultsAvailable: boolean;

    fetchMoreResults: (newSkip: number, newTake: number) => void;

    query: WorkItemSearchRequest;
}

export const LeftPaneContainer = Container.create<StoreName, ActionCreator, IAggregatedState, StoresHub>(
    [
        "searchStore"
    ],
    ({
        searchStoreState,
        selectedItem
    }, props) => {
        const {
            clickFeedbackLink,
            fetchMoreResults,
            changeActiveItem,
            notifyResultsRendered,
            onWorkItemInvoked
        } = props.actionCreator;
        const { response, query, activityId, searchStatus, fetchMoreScenario } = searchStoreState;
        const items = response ? response.results.values : [];
        const itemsLength = items.length;
        const totalResultsCount = response ? response.results.count : 0;
        const searchSucceeded = searchStatus === SearchStatus.Success;

        const needShowMore = response ? (Math.min(totalResultsCount, Constants.WorkItemSearchMaxSkipOrTakeResults) > (query.skipResults + query.takeResults)) : false;

        const infiniteScroll = isFeatureFlagEnabled(props.pageContext, Constants.FeatureFlagConstants.InfiniteScroll, false);

        // Add an empty row to the items to render spinner while fetching the results in case of infinite scroll.
        const newItems = infiniteScroll && needShowMore ? [...items, {} as WorkItemResult] : items;

        if (searchSucceeded || fetchMoreScenario) {
            const mail = format(Constants.DevOpsFeedbackMailToLinkFormat, activityId);
            const activeItemIndex = items.indexOf(selectedItem);

            let idWidth = 0;
            items.forEach(item => {
                const numberOfDigits = getFieldValue(item.fields, "system.id").length;
                if (idWidth < numberOfDigits) {
                    idWidth = numberOfDigits;
                }
            });

            return (
                <>
                    {itemsLength > 0 && (
                        <ResultsInfo
                            infoMessage={getResultsInfoMessage(itemsLength, totalResultsCount)}
                            mailToLink={mail}
                            showFeedbackLink={false}
                            onFeedbackLinkInvoked={clickFeedbackLink}
                        />
                    )}
                    <ScrollableItemsList
                        items={newItems}
                        idWidth={idWidth}
                        activeItemIndex={activeItemIndex}
                        onItemActivation={changeActiveItem}
                        pageContext={props.pageContext}
                        notifyOnRendered={notifyResultsRendered}
                        onWorkItemInvoked={onWorkItemInvoked}
                        itemsLoading={searchStatus === SearchStatus.Loading}
                        renderLoadingRow={() => getLoadMoreRow(searchStatus === SearchStatus.Failed)}
                        infiniteScroll={infiniteScroll}
                        moreResultsAvailable={needShowMore}
                        fetchMoreResults={fetchMoreResults}
                        query={query} />
                </>
            );
        }

        return null;
    }
);

export class ScrollableItemsList extends React.Component<IScrollableItemsListProps, {}> {
    private listRef: React.RefObject<HTMLDivElement | null> = React.createRef<HTMLDivElement>();
    public render(): JSX.Element {
        const { items, itemsLoading, infiniteScroll, fetchMoreResults, moreResultsAvailable, query } = this.props;
        return (
            <Intersection>
                <div
                    // @ts-ignore TypeScript 3.5 update
                    ref={this.listRef}
                    className="v-scroll-auto h-scroll-hidden">
                    {
                        items.length > 0 &&
                        <ItemsList {...this.props} />
                    }
                    {
                        !infiniteScroll &&
                        <ShowMoreRow
                            className="workitem-search-cell"
                            onClick={() => fetchMoreResults(query.takeResults, Constants.WorkItemSearchMaxSkipOrTakeResults)}
                            needShowMore={moreResultsAvailable}
                            itemsLoading={itemsLoading} />
                    }
                </div>
            </Intersection>
        );
    }

    public componentDidMount(): void {
        if (this.listRef.current) {
            this.listRef.current.addEventListener("scroll", this.fetchMoreIfNeeded);
        }
    }

    public componentWillUnmount(): void {
        if (this.listRef.current) {
            this.listRef.current.removeEventListener("scroll", this.fetchMoreIfNeeded);
        }
    }

    private fetchMoreIfNeeded = (): void => {
        const { query, fetchMoreResults, infiniteScroll, moreResultsAvailable } = this.props;
        if (this.listRef && this.listRef.current && infiniteScroll && moreResultsAvailable) {
            const viewPortBottom = this.listRef.current.clientHeight + this.listRef.current.scrollTop;
            const totalHeight = this.listRef.current.scrollHeight;

            if (totalHeight - viewPortBottom < 150) {
                const newSkip = Math.min(query.skipResults + query.takeResults, Constants.WorkItemSearchMaxSkipOrTakeResults);
                const newTake = Constants.WorkItemSearchLoadMoreTakeResults;
                fetchMoreResults(newSkip, newTake);
            }
        }
    }
}

function getResultsInfoMessage(resultsCount: number, totalCount: number): string {
    return resultsCount < totalCount
        ? format(Resources.ShowingWorkItemResultsFormat, resultsCount, totalCount)
        : resultsCount === 1
            ? Resources.ShowingSingleWorkItemResult
            : format(Resources.ShowingNWorkItemResultsFormat, totalCount);
}

function getLoadMoreRow(loadMoreFailed: boolean): JSX.Element {
    return <LoadMore
        error={loadMoreFailed} />;
}